/**
 * @component DashboardLayout
 * @description A centralized layout component for all dashboard pages in the application.
 * Provides consistent structure with header, navigation tabs, filters, and content area.
 * Supports both mobile and desktop views with responsive design.
 *
 * @example
 * // Basic usage
 * <DashboardLayout
 *   title="Technical Staff Dashboard"
 *   description="Manage and process applications"
 *   onRefresh={handleRefetch}
 *   isMobile={isMobile}
 * >
 *   {children}
 * </DashboardLayout>
 *
 * @example
 * With tabs and filters
 * <DashboardLayout
 *   title="Chief Dashboard"
 *   description="Process permit applications"
 *   onRefresh={handleRefetch}
 *   isMobile={isMobile}
 *   mainTabs={['Applications', 'Certificates']}
 *   subTabs={{
 *     'Applications': ['Pending', 'Approved'],
 *     'Certificates': ['Unsigned', 'Signed']
 *   }}
 *   activeMainTab="Applications"
 *   activeSubTab="Pending"
 *   onMainTabChange={handleMainTabChange}
 *   onSubTabChange={handleSubTabChange}
 *   filters={<ApplicationFilters />}
 * >
 *   {children}
 * </DashboardLayout>
 *
 * @typedef {Object} TabCounts
 * @property {number} [tabName] - Number of items for each tab
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Dashboard title
 * @param {string} props.description - Dashboard description
 * @param {Function} props.onRefresh - Callback function for refresh button
 * @param {boolean} props.isMobile - Whether to render mobile or desktop view
 * @param {string[]} [props.mainTabs] - Array of main tab names
 * @param {Object.<string, string[]>} [props.subTabs] - Object mapping main tabs to their sub-tabs
 * @param {string} [props.activeMainTab] - Currently active main tab
 * @param {string} [props.activeSubTab] - Currently active sub-tab
 * @param {Function} [props.onMainTabChange] - Callback for main tab changes
 * @param {Function} [props.onSubTabChange] - Callback for sub-tab changes
 * @param {React.ReactNode} [props.tabDescription] - Description text for current tab
 * @param {React.ReactNode} [props.filters] - Filter components to display
 * @param {React.ReactNode} props.children - Dashboard content
 * @param {TabCounts} [props.tabCounts] - Object containing count for each tab
 * @param {Function} [props.helpTour] - Callback function for help tour
 * @param {number} [props.currentPage] - Current page number
 * @param {number} [props.totalItems] - Total number of items
 * @param {number} [props.itemsPerPage] - Number of items per page
 * @param {Function} [props.onPageChange] - Callback for page changes
 * @param {Function} [props.onItemsPerPageChange] - Callback for items per page changes
 *
 * @returns {React.ReactElement} Rendered dashboard layout
 *
 * @note Layout Structure
 * The layout is organized in the following hierarchy:
 * - Header Card
 *   - Title and Description
 *   - Refresh Button
 *   - Breadcrumb Navigation
 *   - Main Tabs
 *   - Sub Tabs (if applicable)
 *   - Tab Description
 *   - Filters
 * - Content Card
 *   - Children components
 *
 * @note Responsive Design
 * Mobile view:
 * - Tabs convert to dropdown selects
 * - Compact header layout
 * - Stacked navigation
 * - Simplified breadcrumbs
 *
 * Desktop view:
 * - Full tab navigation
 * - Expanded header layout
 * - Keyboard shortcuts display
 * - Complete breadcrumb trail
 *
 * @note Keyboard Navigation
 * Supports keyboard shortcuts:
 * - Alt + ←→: Navigate main tabs
 * - Alt + ↑↓: Navigate sub-tabs
 * Shortcuts are displayed in desktop view
 *
 * @note Breadcrumb Generation
 * Automatically generates breadcrumbs from:
 * - Dashboard title
 * - Active main tab
 * - Active sub-tab (if different from main tab)
 *
 * @note Tab Counts
 * - Displays count badges on tabs when provided
 * - Updates dynamically with tabCounts prop
 * - Handles zero states appropriately
 *
 * @note Styling
 * - Uses Tailwind CSS for responsive design
 * - Consistent with shadcn/ui component library
 * - Dark mode support
 * - Gradient background
 * - Responsive spacing
 *
 * @note Performance
 * - Uses React.memo to prevent unnecessary re-renders
 * - Memoizes breadcrumb generation
 * - Optimizes tab rendering
 *
 * @note Integration Points
 * Used in:
 * - ChiefDashboard
 * - TechnicalStaffDashboard
 * - PENRCENROfficerDashboard
 * - ReceivingReleasingClerkDashboard
 * - BillCollectorDashboard
 * - InspectionSchedulingPage
 *
 * @note Dependencies
 * Required packages:
 * - @/components/ui/button
 * - @/components/ui/card
 * - @/components/ui/tabs
 * - lucide-react (for icons)
 *
 * @note Customization
 * The layout can be customized through:
 * - Tab configurations
 * - Filter components
 * - Description content
 * - Tab counts
 * - Mobile/desktop layouts
 *
 * @note State Management
 * - Maintains internal state for mobile dropdowns
 * - Tab state managed by parent components
 * - Refresh functionality controlled by parent
 *
 * @note Accessibility
 * - Semantic HTML structure
 * - ARIA labels on interactive elements
 * - Keyboard navigation support
 * - Screen reader friendly breadcrumbs
 */

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuGroup,
   DropdownMenuItem,
   DropdownMenuLabel,
   DropdownMenuSeparator,
   DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronRight, ChevronUp, GripVertical, HelpCircle, LayoutGrid, List, Minimize2, RefreshCw, Settings } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const Breadcrumb = ({ items = [] }) => {
   if (!items || items.length === 0) return null;

   return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
         {items.map((item, index) => (
            <React.Fragment key={item}>
               <span>{item}</span>
               {index < items.length - 1 && (
                  <ChevronRight className="h-4 w-4" />
               )}
            </React.Fragment>
         ))}
      </div>
   );
};

const DashboardLayout = ({
   title,
   // description,
   onRefresh,
   isMobile,
   mainTabs = [],
   subTabs = {},
   activeMainTab,
   activeSubTab,
   onMainTabChange,
   onSubTabChange,
   tabDescription,
   filters,
   children,
   tabCounts,
   helpTour,
   viewMode,
   onViewModeChange,
   currentPage,
   totalItems,
   itemsPerPage,
   onPageChange,
   onItemsPerPageChange,
}) => {
   const [isRefreshing, setIsRefreshing] = useState(false);
   const [isCollapsed, setIsCollapsed] = useState(false);
   const [lastChanged, setLastChanged] = useState(null);
   const [width, setWidth] = useState(() => {
      // Get saved width from localStorage or default to 100
      const savedWidth = localStorage.getItem('dashboardWidth');
      return savedWidth ? parseFloat(savedWidth) : 100;
   });
   const [isResizing, setIsResizing] = useState(false);
   const [isCompact, setIsCompact] = useState(() => {
      return localStorage.getItem('dashboardCompactMode') === 'true';
   });
   const [isDropdownOpen, setIsDropdownOpen] = useState(false);
   const dropdownTimeoutRef = useRef(null);
   const [showActionLabels, setShowActionLabels] = useState(() => {
      return localStorage.getItem('dashboardActionLabels') === 'true';
   });

   // Handle mouse down event on the resize handle
   const handleMouseDown = useCallback((e) => {
      e.preventDefault();
      setIsResizing(true);
      // Store initial mouse position and width
      const initialX = e.clientX;
      const initialWidth = width;

      const handleMouseMove = (e) => {
         // Calculate width change based on mouse movement
         const deltaX = e.clientX - initialX;
         const viewportWidth = window.innerWidth;
         const widthChange = (deltaX / viewportWidth) * 100;
         const newWidth = Math.min(Math.max(initialWidth + widthChange, 50), 95);

         setWidth(newWidth);
      };

      const handleMouseUp = () => {
         setIsResizing(false);
         window.removeEventListener('mousemove', handleMouseMove);
         window.removeEventListener('mouseup', handleMouseUp);
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
   }, [width]);

   // Remove the old useEffect for mouse move
   useEffect(() => {
      localStorage.setItem('dashboardWidth', width.toString());
   }, [width]);

   // Save compact mode preference
   useEffect(() => {
      localStorage.setItem('dashboardCompactMode', isCompact);
   }, [isCompact]);

   // Save action labels preference
   useEffect(() => {
      localStorage.setItem('dashboardActionLabels', showActionLabels);
      // console.log('Action labels preference saved:', showActionLabels);
   }, [showActionLabels]);

   // Handle toggle for action labels
   const handleToggleActionLabels = useCallback(() => {
      const newValue = !showActionLabels;
      setShowActionLabels(newValue);
      console.log('Action labels toggled to:', newValue);
   }, [showActionLabels]);

   const hasSubTabs = subTabs && Object.keys(subTabs).length > 0;
   const showMainTabs = mainTabs && mainTabs.length > 0;

   const breadcrumbItems = useMemo(() => {
      if (!title) return [];

      const dashboardTitle = title.split(' - ')[0];
      const items = [dashboardTitle];

      if (activeMainTab) {
         items.push(activeMainTab);
         if (activeSubTab && activeSubTab !== activeMainTab) {
            items.push(activeSubTab);
         }
      }

      return items;
   }, [title, activeMainTab, activeSubTab]);

   const renderSubTabs = useMemo(() => {
      if (!subTabs || !activeMainTab || !subTabs[activeMainTab] || !Array.isArray(subTabs[activeMainTab])) {
         return null;
      }

      return (
         <div className="flex flex-wrap gap-1">
            {subTabs[activeMainTab].map((tab) => {
               if (!tab) return null;

               return (
                  <TabsTrigger
                     key={tab}
                     value={tab}
                     className={cn(
                        "dark:text-white data-[state=active]:bg-white data-[state=active]:text-black dark:data-[state=active]:text-black dark:hover:text-black dark:hover:bg-white",
                        isCompact ? "h-6 px-1.5 text-sm" : "h-7 px-2 text-base font-medium"
                     )}
                  >
                     {tab}
                     {tabCounts && tabCounts[tab] !== undefined && tabCounts[tab] > 0 && (
                        <span className={cn(
                           "ml-1 rounded-full bg-black/10 dark:bg-white/20 font-medium",
                           isCompact ? "px-1 py-0.5 text-[9px]" : "px-1.5 py-0.5 text-[11px]"
                        )}>
                           {tabCounts[tab]}
                        </span>
                     )}
                  </TabsTrigger>
               );
            })}
         </div>
      );
   }, [subTabs, activeMainTab, tabCounts, isCompact]);

   const renderMobileSubTabs = useMemo(() => {
      if (!hasSubTabs || !activeMainTab || !subTabs[activeMainTab] || !Array.isArray(subTabs[activeMainTab])) {
         return null;
      }

      return (
         <select
            value={activeSubTab}
            onChange={(e) => onSubTabChange(e.target.value)}
            className="w-full h-10 px-3 py-2 rounded-md border border-input bg-background text-sm dark:text-white"
         >
            {subTabs[activeMainTab].map((tab) => {
               if (!tab) return null;

               return (
                  <option key={tab} value={tab}>
                     {tab} {tabCounts && tabCounts[tab] !== undefined && tabCounts[tab] > 0 ? `(${tabCounts[tab]})` : ''}
                  </option>
               );
            })}
         </select>
      );
   }, [hasSubTabs, activeMainTab, activeSubTab, subTabs, tabCounts, onSubTabChange]);

   const handleRefresh = async () => {
      setIsRefreshing(true);
      await onRefresh?.();
      // Reset the animation after 1 second
      setTimeout(() => setIsRefreshing(false), 1000);
   };

   const handleMainTabChange = (value) => {
      setLastChanged('main');
      onMainTabChange?.(value);
   };

   const handleSubTabChange = (value) => {
      setLastChanged('sub');
      onSubTabChange?.(value);
   };

   useEffect(() => {
      const handleKeyDown = (e) => {
         if (e.altKey) {
            switch (e.key) {
               case 'ArrowLeft':
               case 'ArrowRight':
                  setLastChanged('main');
                  break;
               case 'ArrowUp':
               case 'ArrowDown':
                  setLastChanged('sub');
                  break;
               default:
                  break;
            }
         }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
   }, []);

   useEffect(() => {
      if (lastChanged) {
         const timer = setTimeout(() => {
            setLastChanged(null);
         }, 2000); // Reset after 2 seconds

         return () => clearTimeout(timer);
      }
   }, [lastChanged]);

   const handleMouseEnter = () => {
      clearTimeout(dropdownTimeoutRef.current);
      if (!isDropdownOpen) {
         setIsDropdownOpen(true);
      }
   };

   const handleMouseLeave = () => {
      dropdownTimeoutRef.current = setTimeout(() => {
         setIsDropdownOpen(false);
      }, 200); // Reduced delay for smoother transition
   };

   return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-gray-800 dark:to-gray-900 transition-colors duration-300 pt-20 pb-8 px-4 sm:px-6 lg:px-8 overflow-x-hidden">
         <div
            className="relative mx-auto space-y-6 pt-2 min-w-0"
            style={{ maxWidth: `${width}%`, transition: isResizing ? 'none' : 'max-width 0.2s ease' }}
         >
            <div
               className={cn(
                  "absolute -right-3 top-1/2 -translate-y-1/2 h-32 w-6 flex items-center justify-center cursor-col-resize group",
                  "hover:bg-accent/50 rounded-md transition-all duration-200",
                  isResizing && "bg-accent/50"
               )}
               onMouseDown={handleMouseDown}
            >
               <div className="relative flex items-center">
                  <div className={cn(
                     "absolute left-1/2 h-24 w-0.5 bg-border/60 rounded-full transition-all duration-200",
                     "group-hover:bg-foreground/30 group-hover:h-28"
                  )} />
                  <GripVertical className={cn(
                     "h-6 w-6 text-muted-foreground/40 transition-all duration-200 z-10",
                     "group-hover:text-foreground/70 group-hover:scale-110"
                  )} />
               </div>
               <div className={cn(
                  "absolute top-1/2 -translate-y-1/2 left-7 opacity-0 bg-popover text-popover-foreground",
                  "px-3 py-2 text-sm rounded-md shadow-md transition-all duration-200",
                  "group-hover:opacity-100 group-hover:left-8",
                  "pointer-events-none whitespace-nowrap"
               )}>
                  Drag to resize
               </div>
            </div>
            <Card className="overflow-hidden">
               <CardHeader className={`flex flex-col space-y-0 ${isCollapsed ? 'pb-2' : 'pb-4'}`}>
                  <div className="flex items-center justify-between w-full">
                     <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-3">
                           <CardTitle className={`text-2xl font-bold text-gray-900 dark:text-white transition-all duration-200 ${isCollapsed ? 'text-xl' : ''}`}>
                              {title}
                           </CardTitle>
                           {isCollapsed && breadcrumbItems.length > 1 && (
                              <div className="flex items-center text-sm text-muted-foreground">
                                 <ChevronRight className="h-4 w-4 mx-1" />
                                 {breadcrumbItems.slice(1).map((item, index) => {
                                    const isMainTab = index === 0;
                                    const isSubTab = index === 1;
                                    const isHighlighted =
                                       (isMainTab && lastChanged === 'main') ||
                                       (isSubTab && lastChanged === 'sub');

                                    return (
                                       <React.Fragment key={item}>
                                          <span className={`transition-colors duration-300 ${isHighlighted
                                             ? 'text-green-600 dark:text-green-400 font-medium'
                                             : 'text-muted-foreground'
                                             }`}>
                                             {item}
                                          </span>
                                          {index < breadcrumbItems.length - 2 && (
                                             <ChevronRight className="h-4 w-4 mx-1" />
                                          )}
                                       </React.Fragment>
                                    );
                                 })}
                              </div>
                           )}
                        </div>
                     </div>
                     <div className="flex items-center gap-4">
                        {/* Show shortcuts when collapsed */}
                        {isCollapsed && showMainTabs && !isMobile && (
                           <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span className="text-muted-foreground/80">Shortcuts:</span>
                              <kbd className="px-2 py-0.5 bg-muted rounded text-xs">Alt + ←→</kbd>
                              <span>main tabs</span>
                              <span className="text-muted-foreground/50">|</span>
                              <kbd className="px-2 py-0.5 bg-muted rounded text-xs">Alt + ↑↓</kbd>
                              <span>sub-tabs</span>
                           </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                           {onRefresh && (
                              <Button
                                 variant="outline"
                                 size="icon"
                                 onClick={handleRefresh}
                                 disabled={isRefreshing}
                                 className="text-muted-foreground hover:text-primary"
                              >
                                 <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
                              </Button>
                           )}

                           <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                              <div
                                 className="relative"
                              >
                                 <DropdownMenuTrigger asChild>
                                    <Button
                                       variant="outline"
                                       size="icon"
                                       className="text-muted-foreground hover:text-primary"
                                    >
                                       <Settings className="h-5 w-5" />
                                    </Button>
                                 </DropdownMenuTrigger>
                                 <DropdownMenuContent
                                    align="end"
                                    className="w-56"
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                 >
                                    <div
                                       onMouseEnter={handleMouseEnter}
                                       onMouseLeave={handleMouseLeave}
                                    >
                                       <DropdownMenuLabel>Dashboard Settings</DropdownMenuLabel>
                                       <DropdownMenuSeparator />
                                       <DropdownMenuGroup>
                                          {helpTour && (
                                             <DropdownMenuItem onClick={helpTour}>
                                                <HelpCircle className="mr-2 h-4 w-4" />
                                                <span>Start guided tour</span>
                                             </DropdownMenuItem>
                                          )}
                                          <DropdownMenuItem onClick={handleToggleActionLabels}>
                                             <List className="mr-2 h-4 w-4" />
                                             <span>{showActionLabels ? 'Hide action labels' : 'Show action labels'}</span>
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => setIsCompact(!isCompact)}>
                                             <Minimize2 className="mr-2 h-4 w-4" />
                                             <span>{isCompact ? 'Normal mode' : 'Compact mode'}</span>
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => setIsCollapsed(!isCollapsed)}>
                                             {isCollapsed ? (
                                                <>
                                                   <ChevronDown className="mr-2 h-4 w-4" />
                                                   <span>Expand header</span>
                                                </>
                                             ) : (
                                                <>
                                                   <ChevronUp className="mr-2 h-4 w-4" />
                                                   <span>Collapse header</span>
                                                </>
                                             )}
                                          </DropdownMenuItem>
                                          {onViewModeChange && (
                                             <DropdownMenuItem onClick={onViewModeChange}>
                                                {viewMode === 'list' ? (
                                                   <>
                                                      <LayoutGrid className="mr-2 h-4 w-4" />
                                                      <span>Switch to grid view</span>
                                                   </>
                                                ) : (
                                                   <>
                                                      <List className="mr-2 h-4 w-4" />
                                                      <span>Switch to list view</span>
                                                   </>
                                                )}
                                             </DropdownMenuItem>
                                          )}
                                       </DropdownMenuGroup>
                                    </div>
                                 </DropdownMenuContent>
                              </div>
                           </DropdownMenu>
                        </div>
                     </div>
                  </div>

                  {!isCollapsed && (
                     <CardContent className="space-y-2 pt-3">
                        {showMainTabs && (
                           isMobile ? (
                              <div className="space-y-4">
                                 {showMainTabs && (
                                    <Breadcrumb
                                       items={breadcrumbItems}
                                    />
                                 )}
                                 <select
                                    value={activeMainTab}
                                    onChange={(e) => onMainTabChange?.(e.target.value)}
                                    className="w-full h-10 px-3 py-2 rounded-md border border-input bg-background text-sm dark:text-white"
                                 >
                                    {mainTabs.map((tab) => {
                                       if (!tab) return null;
                                       return (
                                          <option key={tab} value={tab}>{tab}</option>
                                       );
                                    })}
                                 </select>
                                 {renderMobileSubTabs}
                              </div>
                           ) : (
                              <div className={cn(
                                 "space-y-2 border-t",
                                 isCompact ? "pt-1.5" : "pt-3"
                              )}>
                                 {showMainTabs && (
                                    <div className="flex justify-between items-center">
                                       <Breadcrumb
                                          items={breadcrumbItems}
                                       />
                                       <div className={cn(
                                          "flex items-center gap-1.5 text-muted-foreground",
                                          isCompact ? "text-[10px]" : "text-xs"
                                       )}>
                                          <span className="text-muted-foreground/80">Shortcuts:</span>
                                          <kbd className={cn(
                                             "bg-muted rounded",
                                             isCompact ? "px-1 py-0.5 text-[9px]" : "px-1.5 py-0.5 text-[10px]"
                                          )}>Alt + ←→</kbd>
                                          <span>main tabs</span>
                                          <span className="text-muted-foreground/50 mx-1">|</span>
                                          <kbd className={cn(
                                             "bg-muted rounded",
                                             isCompact ? "px-1 py-0.5 text-[9px]" : "px-1.5 py-0.5 text-[10px]"
                                          )}>Alt + ↑↓</kbd>
                                          <span>sub-tabs</span>
                                       </div>
                                    </div>
                                 )}
                                 <div className="main-tabs">
                                    <Tabs value={activeMainTab} onValueChange={onMainTabChange} className="w-full">
                                       <TabsList className={cn(
                                          "inline-flex w-auto bg-muted/50 gap-1",
                                          isCompact ? "h-8 p-1" : "h-10 p-1.5"
                                       )}>
                                          {mainTabs.map((tab) => (
                                             <TabsTrigger
                                                key={tab}
                                                value={tab}
                                                className={cn(
                                                   "text-muted-foreground dark:text-muted-foreground data-[state=active]:bg-white data-[state=active]:text-black dark:data-[state=active]:text-black min-w-[140px]",
                                                   isCompact ? "h-6 px-3 text-sm" : "h-8 px-4 text-base font-medium"
                                                )}
                                             >
                                                {tab}
                                             </TabsTrigger>
                                          ))}
                                       </TabsList>
                                    </Tabs>
                                 </div>
                                 {hasSubTabs && (
                                    <div className="sub-tabs">
                                       <Tabs value={activeSubTab} onValueChange={onSubTabChange}>
                                          <TabsList className={cn(
                                             "h-auto flex-wrap bg-muted/50",
                                             isCompact ? "gap-1 p-1" : "gap-1.5 p-1.5"
                                          )}>
                                             {renderSubTabs}
                                          </TabsList>
                                       </Tabs>
                                    </div>
                                 )}
                              </div>
                           )
                        )}

                        {tabDescription && (
                           <div className={cn(
                              "text-gray-600 text-muted-foreground dark:text-gray-300",
                              isCompact ? "text-xs pt-2 pl-1" : "text-sm pt-4 pl-1"
                           )}>
                              {tabDescription}
                           </div>
                        )}
                        {filters && (
                           <div className={cn(
                              "border-t",
                              isCompact ? "pt-2" : "pt-3"
                           )}>
                              {filters}
                           </div>
                        )}
                        {totalItems > 0 && (
                           <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t">
                              <div className="flex-1">
                                 {/* Left side can be used for other controls if needed */}
                              </div>
                              <div className="flex flex-wrap items-center gap-4">
                                 <div className="flex items-center gap-2">
                                    <p className="text-sm text-muted-foreground whitespace-nowrap">
                                       Page {currentPage} of {Math.ceil(totalItems / itemsPerPage)}
                                    </p>
                                    <Select
                                       value={itemsPerPage.toString()}
                                       onValueChange={(value) => onItemsPerPageChange(Number(value))}
                                    >
                                       <SelectTrigger className="h-8 w-[80px]">
                                          <SelectValue placeholder={itemsPerPage} />
                                       </SelectTrigger>
                                       <SelectContent>
                                          {[10, 20, 50].map((size) => (
                                             <SelectItem key={size} value={size.toString()}>
                                                {size}
                                             </SelectItem>
                                          ))}
                                       </SelectContent>
                                    </Select>
                                 </div>
                                 <div className="flex gap-2">
                                    <Button
                                       variant="outline"
                                       size="sm"
                                       onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                                       disabled={currentPage === 1}
                                    >
                                       Previous
                                    </Button>
                                    <Button
                                       variant="outline"
                                       size="sm"
                                       onClick={() => onPageChange(currentPage + 1)}
                                       disabled={currentPage >= Math.ceil(totalItems / itemsPerPage)}
                                    >
                                       Next
                                    </Button>
                                 </div>
                              </div>
                           </div>
                        )}
                     </CardContent>
                  )}
               </CardHeader>
               <CardContent className="overflow-auto">
                  <div data-show-action-labels={showActionLabels.toString()}>
                     {children}
                  </div>
               </CardContent>
            </Card>
         </div>
      </div>
   );
};

export default React.memo(DashboardLayout);
